import React, { useState } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import RelationshipSelect from '../common/RelationshipSelect'

const { FiSearch, FiX, FiUserPlus } = FiIcons

const ADD_NEW_PERSON_OPTION = '__add_new_person__'

const relationshipGroups = [
  {
    name: "FAMILY",
    options: [
      // 1st Generation
      { type: 'parent', label: 'Parent (Father/Mother)', group: '1st Generation' },
      { type: 'child', label: 'Child (Son/Daughter)', group: '1st Generation' },
      { type: 'sibling', label: 'Sibling (Brother/Sister)', group: '1st Generation' },
      { type: 'spouse', label: 'Spouse (Husband/Wife)', group: '1st Generation' },
      { type: 'ex_spouse', label: 'Ex-Spouse (Ex-Husband/Ex-Wife)', group: '1st Generation' },
      { type: 'partner', label: 'Partner', group: '1st Generation' },
      { type: 'step_parent', label: 'Step-parent (Stepfather/Stepmother)', group: '1st Generation' },
      { type: 'step_child', label: 'Step-child (Stepson/Stepdaughter)', group: '1st Generation' },
      { type: 'step_sibling', label: 'Step-sibling (Stepbrother/Stepsister)', group: '1st Generation' },
      { type: 'half_sibling', label: 'Half-sibling (Half-brother/Half-sister)', group: '1st Generation' },
      { type: 'adoptive_parent', label: 'Adoptive Parent', group: '1st Generation' },
      { type: 'adoptive_child', label: 'Adopted Child', group: '1st Generation' },
      // 2nd Generation
      { type: 'grandparent', label: 'Grandparent (Grandfather/Grandmother)', group: '2nd Generation' },
      { type: 'grandchild', label: 'Grandchild (Grandson/Granddaughter)', group: '2nd Generation' },
      { type: 'parental_sibling', label: 'Parental Sibling (Aunt/Uncle)', group: '2nd Generation' },
      { type: 'sibling_child', label: 'Sibling\'s Child (Niece/Nephew)', group: '2nd Generation' },
      { type: 'great_grandparent', label: 'Great-Grandparent', group: '2nd Generation' },
      { type: 'great_grandchild', label: 'Great-Grandchild', group: '2nd Generation' },
      // 3rd Generation+
      { type: 'great_great_grandparent', label: 'Great-Great-Grandparent', group: '3rd Generation+' },
      { type: 'grandparent_sibling', label: 'Grandparent\'s Sibling (Great-aunt/Great-uncle)', group: '3rd Generation+' },
      { type: 'grandsibling_child', label: 'Grand-sibling\'s Child (Grandniece/Grandnephew)', group: '3rd Generation+' },
      { type: 'cousin', label: 'Cousin (1st Cousin)', group: '3rd Generation+' },
      { type: 'second_cousin', label: 'Second Cousin', group: '3rd Generation+' },
      { type: 'third_cousin', label: 'Third Cousin', group: '3rd Generation+' },
      { type: 'first_cousin_once_removed', label: 'First Cousin Once Removed', group: '3rd Generation+' },
      { type: 'first_cousin_twice_removed', label: 'First Cousin Twice Removed', group: '3rd Generation+' },
      { type: 'second_cousin_once_removed', label: 'Second Cousin Once Removed', group: '3rd Generation+' },
      // In-Laws
      { type: 'sibling_in_law', label: 'Sibling-in-law (Brother-in-law/Sister-in-law)', group: 'In-Laws' },
      { type: 'parent_in_law', label: 'Parent-in-law (Father-in-law/Mother-in-law)', group: 'In-Laws' },
      { type: 'child_in_law', label: 'Child-in-law (Son-in-law/Daughter-in-law)', group: 'In-Laws' },
      { type: 'cousin_in_law', label: 'Cousin-in-law', group: 'In-Laws' },
      { type: 'sibling_child_in_law', label: 'Niece-in-law / Nephew-in-law', group: 'In-Laws' },
      { type: 'parental_sibling_in_law', label: 'Aunt-in-law / Uncle-in-law', group: 'In-Laws' },
    ]
  },
  {
    name: "OTHER RELATIONSHIPS",
    options: [
      // Professional
      { type: 'colleague', label: 'Colleague', group: 'Professional' },
      { type: 'boss', label: 'Boss', group: 'Professional' },
      { type: 'employee', label: 'Employee', group: 'Professional' },
      // Personal
      { type: 'friend', label: 'Friend', group: 'Personal' }
    ]
  }
];


const RelationshipForm = ({
  person,
  people,
  existingRelationships,
  allRelationships,
  onSubmit,
  onCancel,
  onAddNewPerson,
}) => {
  const [relatedPersonId, setRelatedPersonId] = useState('');
  const [relationshipType, setRelationshipType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const getAvailablePeople = () => {
    if (!people) return [];
    const existingRelIds = new Set(
      (existingRelationships || []).map(rel =>
        rel.person_a_id === person.id ? rel.person_b_id : rel.person_a_id
      )
    );
    return people.filter(p => p.id !== person.id && !existingRelIds.has(p.id));
  };
  const availablePeople = getAvailablePeople();

  const handleSearch = (query) => {
    setSearchTerm(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    const filtered = availablePeople.filter(p =>
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      (p.nicknames && p.nicknames.toLowerCase().includes(query.toLowerCase()))
    );
    setSearchResults(filtered);
  };

  const handleSelectPerson = (personId) => {
    setRelatedPersonId(personId);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleSelectPersonOption = async (value) => {
    if (value === ADD_NEW_PERSON_OPTION) {
      if (!onAddNewPerson) {
        return;
      }

      const previousValue = relatedPersonId;
      const defaultName = searchTerm.trim();
      const name = window.prompt('Enter the name for the new person', defaultName);

      if (!name) {
        setRelatedPersonId(previousValue);
        return;
      }

      try {
        const newPerson = await onAddNewPerson(name.trim());
        if (newPerson?.id) {
          setRelatedPersonId(newPerson.id);
          setSearchTerm('');
          setSearchResults([]);
        } else {
          setRelatedPersonId(previousValue);
        }
      } catch (error) {
        console.error('Failed to add new person:', error);
        setRelatedPersonId(previousValue);
      }
      return;
    }

    setRelatedPersonId(value);
  };

  const handleAddNewPersonLink = async () => {
    if (!onAddNewPerson) return;

    try {
      const newPerson = await onAddNewPerson('');
      if (newPerson?.id) {
        setRelatedPersonId(newPerson.id);
        setSearchTerm('');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Failed to add new person:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit([{
        relatedPersonId: relatedPersonId,
        relationshipType: relationshipType,
      }]);
    } catch (error) {
      console.error("Failed to add relationship:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- Relationship Helpers ---
  const getPersonRelationships = (personId) => {
    if (!allRelationships) return [];
    return allRelationships
      .filter(rel => rel.person_a_id === personId || rel.person_b_id === personId)
      .map(rel => {
        const isPersonA = rel.person_a_id === personId;
        const otherPersonId = isPersonA ? rel.person_b_id : rel.person_a_id;
        const relationshipType = isPersonA ? rel.relationship_type_b : rel.relationship_type;
        const otherPerson = people.find(p => p.id === otherPersonId);
        return {
          personName: otherPerson?.name || 'Unknown',
          relationshipType: relationshipType || 'unknown'
        };
      })
      .slice(0, 2);
  };

  const formatRelationshipInfo = (relationships) => {
    if (!relationships || relationships.length === 0) return '';
    return relationships.map(rel => `${rel.personName}(${rel.relationshipType})`).join(', ');
  };

  const selectedPersonName = people.find(p => p.id === relatedPersonId)?.name || '';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
    >
      <h3 className="text-lg font-bold text-gray-800 mb-4">Add Relationship for {person.name}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Person</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SafeIcon icon={FiSearch} className="text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search people..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md">
            {(searchTerm ? searchResults : availablePeople).map(p => {
              const relationships = getPersonRelationships(p.id);
              const relationshipInfo = formatRelationshipInfo(relationships);
              return (
                <div
                  key={p.id}
                  className={`p-3 cursor-pointer hover:bg-blue-50 ${relatedPersonId === p.id ? 'bg-blue-100' : ''}`}
                  onClick={() => handleSelectPerson(p.id)}
                >
                  <div className="flex items-center space-x-2 min-w-0">
                    <div className="font-medium truncate">{p.name}</div>
                    {relationshipInfo && (
                      <div className="text-xs text-purple-800 bg-purple-100 px-2 py-0.5 rounded-full whitespace-nowrap truncate">
                        {relationshipInfo}
                      </div>
                    )}
                  </div>
                  {p.nicknames && <div className="text-sm text-gray-500 mt-1 truncate">({p.nicknames})</div>}
                </div>
              );
            })}
            {searchTerm && searchResults.length === 0 && (
              <div className="p-3 text-center text-gray-500">
                <p>No results for "{searchTerm}"</p>
                {onAddNewPerson && (
                  <button
                    type="button"
                    onClick={async () => {
                      const trimmedSearch = searchTerm.trim()
                      if (!trimmedSearch) return
                      try {
                        const newPerson = await onAddNewPerson(trimmedSearch)
                        if (newPerson?.id) {
                          setRelatedPersonId(newPerson.id)
                          setSearchTerm('')
                          setSearchResults([])
                        }
                      } catch (error) {
                        console.error('Failed to add new person:', error)
                      }
                    }}
                    className="mt-2 w-full text-left px-3 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    + Add "{searchTerm}" as a new person
                  </button>
                )}
              </div>
            )}
          </div>

          <select
            value={relatedPersonId}
            onChange={(e) => handleSelectPersonOption(e.target.value)}
            className="w-full mt-2 pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="" disabled>Select a person</option>
            {availablePeople.map(p => {
              const relationshipInfo = formatRelationshipInfo(getPersonRelationships(p.id));
              return <option key={p.id} value={p.id}>{p.name}{relationshipInfo && ` (${relationshipInfo})`}</option>
            })}
            {onAddNewPerson && (
              <option value={ADD_NEW_PERSON_OPTION}>+ Add new person</option>
            )}
          </select>

          {onAddNewPerson && (
            <button
              type="button"
              onClick={handleAddNewPersonLink}
              className="mt-2 flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 hover:underline focus:outline-none"
            >
              <SafeIcon icon={FiUserPlus} className="text-base" />
              <span>Add new person</span>
            </button>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Is their...</label>
          <RelationshipSelect
            value={relationshipType}
            onChange={setRelationshipType}
            relationshipGroups={relationshipGroups}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!relatedPersonId || !relationshipType || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Adding...' : 'Add Relationship'}
          </button>
        </div>
      </form>
    </motion.div>
  )
}

export default RelationshipForm